import type { AuthConfig } from "@atlas/auth";
import type { BillingApiConfig } from "@atlas/billing";
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Application configuration from environment variables
 */
export const config = {
  cognito: {
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "",
    userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? "",
    oauthDomain: import.meta.env.VITE_COGNITO_OAUTH_DOMAIN ?? "",
    redirectSignIn: `${window.location.origin}/auth/callback`,
    redirectSignOut: `${window.location.origin}/login`,
  } satisfies AuthConfig,

  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? "",
  },

  chat: {
    url: import.meta.env.VITE_CHAT_URL ?? "",
  },

  stripe: {
    publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "",
    prices: {
      starter: {
        monthly: import.meta.env.VITE_STRIPE_STARTER_MONTHLY_PRICE_ID ?? "",
        yearly: import.meta.env.VITE_STRIPE_STARTER_YEARLY_PRICE_ID ?? "",
      },
      pro: {
        monthly: import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID ?? "",
        yearly: import.meta.env.VITE_STRIPE_PRO_YEARLY_PRICE_ID ?? "",
      },
      byok: {
        monthly: import.meta.env.VITE_STRIPE_BYOK_MONTHLY_PRICE_ID ?? "",
        yearly: import.meta.env.VITE_STRIPE_BYOK_YEARLY_PRICE_ID ?? "",
      },
    },
  },
} as const;

/**
 * Singleton billing API configuration with access token getter.
 * Must be a stable reference to avoid React re-render loops.
 */
export const billingApiConfig: BillingApiConfig = {
  baseUrl: config.api.baseUrl,
  getAccessToken: async (): Promise<string> => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new Error("No ID token available");
    }
    return token;
  },
};

/**
 * Token top-up price configuration
 */
export const TOKEN_TOPUP_PRICES = {
  small: {
    tokens: 250000,
    priceId: import.meta.env.VITE_STRIPE_TOPUP_250K_PRICE_ID ?? "",
    amount: 499, // $4.99
  },
  medium: {
    tokens: 1000000,
    priceId: import.meta.env.VITE_STRIPE_TOPUP_1M_PRICE_ID ?? "",
    amount: 1499, // $14.99
  },
  large: {
    tokens: 3000000,
    priceId: import.meta.env.VITE_STRIPE_TOPUP_3M_PRICE_ID ?? "",
    amount: 3999, // $39.99
  },
} as const;
